import React, { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { Header, ModelInfo } from "build-ts/metadata";
import { ColumnType } from "build-ts/enums";
import { getAllKGColumns, getDataModelHeaders } from "services/DataModelService";
import showToast from "hooks/useCustomToast";
import { deleteMetric, listMetrics, getBenchmarkById, deleteBenchmark, deleteKgColumn } from "services/MetricService";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";

interface ContextType {
  metricList: Header[];
  setMetricList: React.Dispatch<React.SetStateAction<Header[]>>;
  modelList: Header[];
  setModelList: React.Dispatch<React.SetStateAction<Header[]>>;
  selectedMetric: Header | null;
  setSelectedMetric: React.Dispatch<React.SetStateAction<Header | null>>;
  editMetricDefinition: boolean;
  setEditMetricDefinition: (editMetricDefinition: boolean) => void;
  viewMKG: boolean;
  setViewMKG: (viewMKG: boolean) => void;

  // Shared dependencies state — set by any delete that returns blocking dependencies
  dependencies: Header[];
  setDependencies: (dependencies: Header[]) => void;
  showDependenciesModal: boolean;
  setShowDependenciesModal: (show: boolean) => void;

  fetchAllMetrics: () => Promise<void>;
  fetchModelList: () => Promise<void>;
  handleEditMetricDefinitionModal: (metricHeader: Header) => void;
  handleViewMKGModal: (metricHeader: Header) => void;
  deleteMetricById: (id: string) => Promise<void>;

  // kg column
  selectedKgColumn: Header | null;
  setSelectedKgColumn: React.Dispatch<React.SetStateAction<Header | null>>;
  editColumn: boolean;
  setEditColumn: (editColumn: boolean) => void;
  handleEditColumnModal: (columnHeader: Header) => void;
  kgColumns: Header[] | null;
  fetchKgColumns: () => Promise<Header[]>;
  deleteKgColumnById: (id: string) => Promise<void>;
  /** KG column IDs grouped by column type — derived from kgColumns, no extra API call */
  attributeKgIds: Set<string>;
  measureKgIds: Set<string>;
  temporalKgIds: Set<string>;

  // benchmark
  benchmarkList: Header[];
  setBenchmarkList: React.Dispatch<React.SetStateAction<Header[]>>;
  selectedBenchmark: Header | null;
  setSelectedBenchmark: React.Dispatch<React.SetStateAction<Header | null>>;
  selectedBenchmarkId: BenchmarkEntityProto | null;
  setSelectedBenchmarkId: (benchmark: BenchmarkEntityProto | null) => void;
  editBenchmark: boolean;
  setEditBenchmark: (editBenchmark: boolean) => void;
  handleEditBenchmark: (benchmark: Header) => void;
  fetchBenchmarkById: (id: string) => Promise<void>;
  deleteBenchmarkById: (id: string) => Promise<void>;
  modelInfoMap: Map<string, ModelInfo>;
  setModelInfoMap: React.Dispatch<React.SetStateAction<Map<string, ModelInfo>>>;
  dependencyMap: Map<string, Map<string, Header[]>>;
  setDependencyMap: React.Dispatch<React.SetStateAction<Map<string, Map<string, Header[]>>>>;
}

const ApiContext = createContext<ContextType | undefined>(undefined);

export const useConfigurationContext = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApiContext must be used within an ApiProvider");
  }
  return context;
};

export const ConfigurationContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [metricList, setMetricList] = useState<Header[]>([]);
  const [modelList, setModelList] = useState<Header[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<Header | null>(null);
  const [editMetricDefinition, setEditMetricDefinition] = useState<boolean>(false);
  const [viewMKG, setViewMKG] = useState<boolean>(false);

  // Shared dependencies state
  const [dependencies, setDependencies] = useState<Header[]>([]);
  const [showDependenciesModal, setShowDependenciesModal] = useState<boolean>(false);

  // state for kg column
  const [selectedKgColumn, setSelectedKgColumn] = useState<Header | null>(null);
  const [editColumn, setEditColumn] = useState<boolean>(false);
  const [kgColumns, setKgColumns] = useState<Header[] | null>(null);

  const attributeKgIds = useMemo<Set<string>>(
    () => new Set((kgColumns ?? []).filter(h => h.column_type === ColumnType.ATTRIBUTE).map(h => h.id).filter((id): id is string => Boolean(id))),
    [kgColumns]
  );
  const measureKgIds = useMemo<Set<string>>(
    () => new Set((kgColumns ?? []).filter(h => h.column_type === ColumnType.MEASURE).map(h => h.id).filter((id): id is string => Boolean(id))),
    [kgColumns]
  );
  const temporalKgIds = useMemo<Set<string>>(
    () => new Set((kgColumns ?? []).filter(h => h.column_type === ColumnType.TEMPORAL).map(h => h.id).filter((id): id is string => Boolean(id))),
    [kgColumns]
  );

  // state for benchmark
  const [benchmarkList, setBenchmarkList] = useState<Header[]>([]);
  const [selectedBenchmark, setSelectedBenchmark] = useState<Header | null>(null);
  const [selectedBenchmarkId, setSelectedBenchmarkId] = useState<BenchmarkEntityProto | null>(null);
  const [editBenchmark, setEditBenchmark] = useState<boolean>(false);

  /** Map<modelId, ModelInfo> — gives total column count and ts_worksheet_id */
  const [modelInfoMap, setModelInfoMap] = useState<Map<string, ModelInfo>>(new Map());
  /** Map<modelId, Map<logicalColumnId, Header[]>> — dependency headers per logical column */
  const [dependencyMap, setDependencyMap] = useState<Map<string, Map<string, Header[]>>>(new Map());

  const fetchKgColumns = async (): Promise<Header[]> => {
    try {
      const kgColumns = await getAllKGColumns();
      if (kgColumns) {
        setKgColumns(kgColumns);
        return kgColumns;
      }
      return [];
    } catch (error) {
      showToast("Error fetching KG columns", "", "error");
      return [];
    }
  };

  const fetchModelList = async () => {
    try {
      const data = await getDataModelHeaders(false);
      if (data) setModelList(data);
    } catch {
      showToast("Error in REACT_APP_LIST_DATMODEL", "", "error");
    }
  };

  const handleEditColumnModal = (columnHeader: Header) => {
    setEditColumn(true);
    setSelectedKgColumn(columnHeader);
  };

  const handleEditBenchmark = (benchmark: Header) => {
    setEditBenchmark(true);
    setSelectedBenchmark(benchmark);
    fetchBenchmarkById(benchmark?.id || "");
  };

  const fetchBenchmarkById = async (id: string) => {
    try {
      const data = await getBenchmarkById(id, true);
      if (data) {
        setSelectedBenchmarkId(data);
      }
    } catch (error) {
      showToast("Error fetching benchmark details", "", "error");
    }
  };

  const fetchAllMetrics = async () => {
    try {
      const metrics = await listMetrics("");
      setMetricList(metrics);
    } catch (error) {
      showToast("Error fetching metrics", "", "error");
    }
  };

  const handleEditMetricDefinitionModal = (metricHeader: Header) => {
    setSelectedMetric(metricHeader);
    setViewMKG(false);
    setEditMetricDefinition(true);
  };

  const handleViewMKGModal = (metricHeader: Header) => {
    setSelectedMetric(metricHeader);
    setEditMetricDefinition(false);
    setViewMKG(true);
  };

  const deleteMetricById = async (id: string) => {
    const response: any = await deleteMetric(id);
    if (response) {
      setDependencies(response);
      setShowDependenciesModal(true);
    } else {
      showToast("Metric deleted successfully", "", "success");
      window.location.reload();
    }
  };

  const deleteBenchmarkById = async (id: string) => {
    const response: any = await deleteBenchmark(id);
    if (response) {
      setDependencies(response);
      setShowDependenciesModal(true);
    } else {
      showToast("Benchmark deleted successfully", "", "success");
      window.location.reload();
    }
  };

  const deleteKgColumnById = async (id: string) => {
    const response: any = await deleteKgColumn(id);
    if (response) {
      setDependencies(response);
      setShowDependenciesModal(true);
    } else {
      showToast("KG column deleted successfully", "", "success");
      window.location.reload();
    }
  };

  return (
    <ApiContext.Provider
      value={{
        metricList,
        setMetricList,
        modelList,
        setModelList,
        selectedMetric,
        setSelectedMetric,
        editMetricDefinition,
        setEditMetricDefinition,
        viewMKG,
        setViewMKG,

        dependencies,
        setDependencies,
        showDependenciesModal,
        setShowDependenciesModal,

        fetchAllMetrics,
        fetchModelList,
        handleEditMetricDefinitionModal,
        handleViewMKGModal,
        deleteMetricById,

        // kg column
        selectedKgColumn,
        setSelectedKgColumn,
        editColumn,
        setEditColumn,
        handleEditColumnModal,
        kgColumns,
        fetchKgColumns,
        deleteKgColumnById,
        attributeKgIds,
        measureKgIds,
        temporalKgIds,

        // benchmark
        benchmarkList,
        setBenchmarkList,
        selectedBenchmark,
        setSelectedBenchmark,
        selectedBenchmarkId,
        setSelectedBenchmarkId,
        editBenchmark,
        setEditBenchmark,
        handleEditBenchmark,
        fetchBenchmarkById,
        deleteBenchmarkById,
        modelInfoMap,
        setModelInfoMap,
        dependencyMap,
        setDependencyMap,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};
